import React, { useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Upload,
  Image,
  App as AntApp,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import { getStorageUrl } from '../utils/storageUtils';

const { Title } = Typography;
const { Option } = Select;

const Brands = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [logoFileList, setLogoFileList] = useState([]);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { message, modal } = AntApp.useApp();

  // Fetch brands with API
  const { data: brands = [] } = useQuery(
    'brands',
    async () => {
      const response = await api.get('/admin/brands');
      return response.data?.data || response.data || [];
    },
    {
      onError: (error) => {
        console.error('Failed to fetch brands:', error);
        // Fallback to mock data if API fails
        return [
          {
            id: 1,
            name: 'Apple',
            description: 'Technology company',
            status: 'active',
            products: 15,
            logo: null,
          },
          {
            id: 2,
            name: 'Samsung',
            description: 'Electronics manufacturer',
            status: 'active',
            products: 22,
            logo: null,
          },
          {
            id: 3,
            name: 'Nike',
            description: 'Sports apparel',
            status: 'active',
            products: 18,
            logo: null,
          },
          {
            id: 4,
            name: 'Sony',
            description: 'Electronics and entertainment',
            status: 'active',
            products: 12,
            logo: null,
          },
        ];
      },
    },
  );

  // Create brand mutation
  const createBrandMutation = useMutation(
    async (values) => {
      const formData = new FormData();

      // Add all form fields
      Object.keys(values).forEach((key) => {
        if (key !== 'logo' && values[key] !== undefined && values[key] !== null) {
          formData.append(key, values[key]);
        }
      });

      // Add file from logoFileList
      if (logoFileList.length > 0 && logoFileList[0].originFileObj) {
        formData.append('logo', logoFileList[0].originFileObj);
      }

      const response = await api.post('/admin/brands', formData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Brand created successfully');
        setIsModalVisible(false);
        setEditingBrand(null);
        form.resetFields();
        queryClient.invalidateQueries('brands');
      },
      onError: (_error) => {
        console.error('Failed to create brand:', _error);
        message.error('Failed to create brand');
      },
    },
  );

  // Update brand mutation
  const updateBrandMutation = useMutation(
    async ({ id, ...values }) => {
      const formData = new FormData();

      // Add all form fields
      Object.keys(values).forEach((key) => {
        if (key !== 'logo' && values[key] !== undefined && values[key] !== null) {
          formData.append(key, values[key]);
        }
      });

      // Add file from logoFileList if a new file was uploaded
      if (logoFileList.length > 0 && logoFileList[0].originFileObj) {
        formData.append('logo', logoFileList[0].originFileObj);
      }

      // Add method override for PUT
      formData.append('_method', 'PUT');

      const response = await api.post(`/admin/brands/${id}`, formData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Brand updated successfully');
        setIsModalVisible(false);
        setEditingBrand(null);
        form.resetFields();
        queryClient.invalidateQueries('brands');
      },
      onError: (_error) => {
        console.error('Failed to update brand:', _error);
        message.error('Failed to update brand');
      },
    },
  );

  // Delete brand mutation
  const deleteBrandMutation = useMutation(
    async (id) => {
      await api.delete(`/admin/brands/${id}`);
    },
    {
      onSuccess: () => {
        message.success('Brand deleted successfully');
        queryClient.invalidateQueries('brands');
      },
      onError: (_error) => {
        console.error('Failed to delete brand:', _error);
        message.error('Failed to delete brand');
      },
    },
  );

  const columns = [
    {
      title: 'Logo',
      dataIndex: 'logo',
      key: 'logo',
      render: (logo, record) => {
        const logoUrl = logo?.url || logo;
        const src = logoUrl
          ? getStorageUrl(logoUrl)
          : `https://via.placeholder.com/50x50?text=${record.name?.charAt(0) || 'B'}`;

        return (
          <Image
            width={50}
            height={50}
            src={src}
            style={{ objectFit: 'cover', borderRadius: '8px' }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6mAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAOxAAADsQBlSsOGwAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAADISURBVHic7cEBDQAAAMKg909tDjegAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIBby6EDAAFoB1fZAAAAAElFTkSuQmCC"
            preview={{
              mask: 'View Logo',
            }}
          />
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
    },
    {
      title: 'Products',
      dataIndex: 'products',
      key: 'products',
      sorter: (a, b) => (parseInt(a.products) || 0) - (parseInt(b.products) || 0),
      render: (products) => products || 0,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
      render: (status) => <Tag color={status === 'active' ? 'green' : 'red'}>{status}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    form.setFieldsValue(brand);
    // Load existing logo into file list
    if (brand.logo) {
      const logoUrl = brand.logo?.url || brand.logo;
      if (logoUrl) {
        setLogoFileList([
          {
            uid: 'existing-logo',
            name: logoUrl,
            status: 'done',
            url: getStorageUrl(logoUrl),
          },
        ]);
      } else {
        setLogoFileList([]);
      }
    } else {
      setLogoFileList([]);
    }
    setIsModalVisible(true);
  };

  const handleDelete = (record) => {
    modal.confirm({
      title: 'Are you sure you want to delete this brand?',
      content: `Brand: ${record.name}`,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => {
        deleteBrandMutation.mutate(record.id);
      },
      onCancel: () => {
        // Handle cancel if needed
      },
    });
  };

  const handleSubmit = (values) => {
    if (editingBrand) {
      updateBrandMutation.mutate({ id: editingBrand.id, ...values });
    } else {
      createBrandMutation.mutate(values);
    }
  };

  return (
    <div>
      <Title level={2}>Brand Management</Title>
      <Card>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsModalVisible(true)}
          style={{ marginBottom: 16 }}
        >
          Add Brand
        </Button>
        <Table
          columns={columns}
          dataSource={brands}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} brands`,
          }}
        />
      </Card>

      <Modal
        title={editingBrand ? 'Edit Brand' : 'Add Brand'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingBrand(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Brand Name"
            rules={[{ required: true, message: 'Please input brand name!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please input description!' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select status!' }]}
          >
            <Select>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Form.Item>

          <Form.Item name="logo" label="Logo">
            <Upload
              name="logo"
              listType="picture"
              maxCount={1}
              beforeUpload={() => false}
              fileList={logoFileList}
              onChange={({ fileList }) => setLogoFileList(fileList)}
              onRemove={() => {
                setLogoFileList([]);
                return true;
              }}
            >
              <Button icon={<UploadOutlined />}>
                {logoFileList.length === 0 ? 'Click to Upload' : 'Replace Logo'}
              </Button>
            </Upload>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingBrand ? 'Update' : 'Create'}
              </Button>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  setEditingBrand(null);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Brands;
